import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    isBusinessOwner,
    isDeclinedAdmin,
    canSeeBusinessSettings,
    isBloggerBusiness,
} from '../../../../../utils/accessService';
import { analyticsService, EventSource } from '../../../../../utils/analyticsService';
import { getIsMobile } from '../../../../../utils/getIsMobile';
import { getMapsClickout } from '../../../../../utils/getMapsClickout';
import { ymTrackSettingsClick } from '../../../../../utils/metrika';
import { useExperimentFlag } from '../../../../../utils/useExperimentFlag';
import { useIsAdvertisingCatalogEnabled } from '../../../../../utils/useIsAdvertisingCatalogEnabled';
import { useTranslation } from '../../../../../utils/useTranslation';
import { AdvertisingButton } from '../../../../advertising/AdvertisingButton/AdvertisingButton';
import { CatalogButton } from '../../../../catalog/CatalogButton';
import { IconFollow } from '../../../../icon/IconFollow';
import { IconSettings } from '../../../../icon/IconSettings';
import { IconUnfollowChecked } from '../../../../icon/IconUnfollowChecked';
import { Button } from '../../../../input/Button';
import { Flex } from '../../../../layout/Flex';
import { BookingButton } from '../../../BusinessCard/parts/BookingButton/BookingButton';
import { ShareButton } from '../../../BusinessCard/parts/ShareButton/ShareButton';
import { ProfileActionsProps } from './types';

export const ProfileActions = ({
    business,
    user,
    onSubscribe: onBaseSubscribe,
    onShare,
    onCatalogClick,
}: ProfileActionsProps) => {
    const t = useTranslation();

    const isBlogger = isBloggerBusiness(business.type);
    const { isMobile } = getIsMobile();
    const bookingButtonText = isMobile ? t('Забронировать') : t('Забронировать онлайн');
    const isAdvertisingCatalogEnabled =
        useIsAdvertisingCatalogEnabled() && isBusinessOwner(business.id, user);

    const onSubscribe = useCallback(() => onBaseSubscribe(business), [business, onBaseSubscribe]);

    const onSettingsClick = useCallback(() => {
        ymTrackSettingsClick({
            page: 'Профиль бизнеса',
            userLogin: user?.login,
            businessId: user?.profile?.publicId,
        });
    }, [user?.login, user?.profile?.publicId]);

    const disableServiceBooking = Number(useExperimentFlag('disable_service_booking')) === 1;
    const dikidiUrl = business.links?.dikidiUrl ?? '';
    const mapsClickout = getMapsClickout(business.clickouts);
    const hasMapsBooking = Boolean(mapsClickout?.url);
    const hasDikidiBooking = Boolean(
        !disableServiceBooking && business?.isDikidiApproved && dikidiUrl,
    );
    const hasBookingButton = hasDikidiBooking || hasMapsBooking;

    const onBookingButtonClick = useCallback(() => {
        analyticsService.trackClickOut({
            businessId: business.id,
            clickoutUrl: hasMapsBooking ? mapsClickout?.url : dikidiUrl,
            eventSource: EventSource.BUSINESS,
            target: 'business_site',
        });
    }, [business, dikidiUrl, hasMapsBooking, mapsClickout?.url]);

    return (
        <Flex direction="row" spacing={1}>
            {isAdvertisingCatalogEnabled ? (
                <AdvertisingButton
                    flexGrow={1}
                    width={{ xs: 'auto', sm: 'calc(50% - 4px)' }}
                    target="_self"
                />
            ) : null}
            {!isDeclinedAdmin(user?.role) && !canSeeBusinessSettings(business.id, user) ? (
                <Button
                    flexGrow={1}
                    size="m"
                    variant="filled"
                    color={business.subscribed ? 'secondary' : 'primary'}
                    spacing={0.5}
                    onClick={onSubscribe}
                    width={isAdvertisingCatalogEnabled ? 'calc(50% - 4px)' : 'auto'}
                >
                    {business.subscribed ? (
                        <IconUnfollowChecked fontSize={20} />
                    ) : (
                        <IconFollow fontSize={20} />
                    )}
                    {business.subscribed ? t('Вы подписаны') : t('Подписаться')}
                </Button>
            ) : null}
            {business.catalogUrl && !business.isService && !hasBookingButton ? (
                <CatalogButton
                    size="m"
                    spacing={0.5}
                    business={business}
                    onClick={onCatalogClick}
                    outlineIcon
                />
            ) : null}
            {hasBookingButton ? (
                <BookingButton
                    url={hasMapsBooking ? mapsClickout?.url ?? '' : dikidiUrl}
                    text={hasMapsBooking ? bookingButtonText : undefined} // для тачей просто Забронировать
                    onClick={onBookingButtonClick}
                />
            ) : null}
            {isBlogger || (business.isService && business.links?.site) ? (
                <ShareButton onShare={onShare} />
            ) : null}
            {canSeeBusinessSettings(business.id, user) ? (
                <Button
                    as={Link}
                    size="m"
                    flexGrow={1}
                    variant="filled"
                    color="secondary"
                    spacing={0.5}
                    to={business.editLink}
                    onClick={onSettingsClick}
                    width={isAdvertisingCatalogEnabled ? 'calc(50% - 4px)' : 'auto'}
                >
                    <IconSettings size={20} />
                    {t('Настройки')}
                </Button>
            ) : null}
        </Flex>
    );
};
